import type { PortalLinkedRecord } from "./types";

/**
 * What a panel is called in the switcher and the chooser.
 *
 * The course at a training centre, where a student switches between courses
 * with their tutor rather than between "therapists"; the professional's name
 * everywhere else.
 */
export function recordLabel(record: PortalLinkedRecord): string {
    return record.course_name ?? record.therapist_name ?? record.center_name ?? "";
}

/** The line under the label: the trainer for a course, the centre otherwise. */
export function recordSubtitle(record: PortalLinkedRecord): string | null {
    if (record.course_name && record.therapist_name) return record.therapist_name;
    return record.center_name;
}

/** What the patient is actually choosing between. */
export type PanelChoice = "course" | "professional" | "mentor" | "panel";

/**
 * Which word to use for the choice, from what the account holds.
 *
 * A training centre's student picks a course; everybody else picks a
 * professional — a solo therapist and a therapy centre read the same, because
 * in both the person is choosing who they are seeing. An account that mixes
 * the two falls back to the neutral word rather than calling a course a
 * therapist or the other way round.
 */
export function panelChoice(records: PortalLinkedRecord[]): PanelChoice {
    if (records.length === 0) return "panel";
    const courses = records.filter((record) => record.course_name).length;
    if (courses === records.length) return "course";
    if (courses > 0) return "panel";
    // No courses name these panels. They are still a school's if the people
    // running them are mentors — a student between courses is still a student.
    const mentors = records.filter((record) => record.professional_term === "trainer").length;
    if (mentors === records.length) return "mentor";
    if (mentors > 0) return "panel";
    return "professional";
}

/** What kind of thing a name in the panel list is. */
export type LabelKind = "course" | "professional" | "mentor" | "center";

/**
 * What to call the person running this panel.
 *
 * A training centre teaches, so the person behind a course is a mentor.
 * Calling them the patient's therapist would describe a relationship the
 * student is not in.
 */
function professionalKind(record: PortalLinkedRecord): LabelKind {
    return record.professional_term === "trainer" ? "mentor" : "professional";
}

/** The kind of the name that titles the panel. */
export function recordKind(record: PortalLinkedRecord): LabelKind {
    if (record.course_name) return "course";
    if (record.therapist_name) return professionalKind(record);
    return "center";
}

/** The kind of the line underneath it, when there is one. */
export function recordSubtitleKind(record: PortalLinkedRecord): LabelKind | null {
    if (record.course_name && record.therapist_name) return professionalKind(record);
    if (record.center_name) return "center";
    return null;
}
